import { useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";
import { GLIDE_MS } from "@libs/checkout-core";
import { SETTLE_EASE } from "./motion";

/**
 * FLIP: when `key` changes, the element is drawn where it was a moment ago
 * and glides to where the new layout put it. Centre to centre, so an element
 * that also changes width still travels straight.
 *
 * The old position is read while rendering the change, when the DOM still
 * shows the old layout — not remembered from an earlier commit, which a CSS
 * transition (a fold closing above) may since have moved it away from.
 */
export function useFlip(
  ref: RefObject<HTMLElement | null>,
  key: unknown,
  { duration = GLIDE_MS, easing = SETTLE_EASE } = {},
) {
  const lastKey = useRef(key);
  const from = useRef<DOMRect | null>(null);
  const running = useRef<Animation | null>(null);
  if (lastKey.current !== key)
    from.current = ref.current?.getBoundingClientRect() ?? null;
  useLayoutEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    const prev = from.current;
    from.current = null;
    const el = ref.current;
    if (!el || !prev || typeof el.animate !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const next = el.getBoundingClientRect();
    const dx = prev.left + prev.width / 2 - (next.left + next.width / 2);
    const dy = prev.top + prev.height / 2 - (next.top + next.height / 2);
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
    // A move still in flight gives way: the new one starts from where the
    // element is drawn right now (read above), so nothing jumps.
    running.current?.cancel();
    running.current = el.animate(
      [
        { transform: `translate(${dx}px, ${dy}px)` },
        { transform: "translate(0, 0)" },
      ],
      { duration, easing },
    );
  });
}
